// RootStore - combines all stores and provides React context

import { makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
  BillingStore,
  billingStore,
} from '../features/billing/stores/BillingStore';
import { NavigationService } from '../services/NavigationService';
import { EventInstanceStore, eventInstanceStore } from './EventInstanceStore';
import { ScheduleStore, scheduleStore } from './ScheduleStore';
import { SkillLevelsStore, skillLevelsStore } from './SkillLevelsStore';
import { UserStore, userStore } from './UserStore';
import { WorkspaceStore, workspaceStore } from './WorkspaceStore';

export class RootStore {
  userStore: UserStore;
  workspaceStore: WorkspaceStore;
  billingStore: BillingStore;
  eventInstanceStore: EventInstanceStore;
  scheduleStore: ScheduleStore;
  skillLevelsStore: SkillLevelsStore;
  navigationService: NavigationService;

  @observable isInitialized = false;

  constructor() {
    makeObservable(this);

    this.userStore = userStore;
    this.workspaceStore = workspaceStore;
    this.billingStore = billingStore;
    this.eventInstanceStore = eventInstanceStore;
    this.scheduleStore = scheduleStore;
    this.skillLevelsStore = skillLevelsStore;
    this.navigationService = new NavigationService();
  }

  // Initialize all stores
  async initialize(navigate: NavigateFunction): Promise<void> {
    if (this.isInitialized) {
      return; // Prevent duplicate initialization
    }

    this.navigationService.setNavigate(navigate);

    await Promise.all([
      this.userStore.initialize(),
      this.workspaceStore.initialize(),
      this.scheduleStore.initialize(),
      this.skillLevelsStore.loadSkillLevels(),
    ]);

    await this.eventInstanceStore.initialize();

    runInAction(() => {
      this.isInitialized = true;
    });
  }

  // Destroy all stores (cleanup)
  // destroy(): void {
  //   this.userStore.destroy();
  //   this.workspaceStore.destroy();
  //   this.scheduleStore.destroy();
  //   this.eventInstanceStore.destroy();
  //   this.billingStore.destroy();
  //   this.isInitialized = false;
  // }
}

// Create context for React
export const StoreContext = createContext<RootStore | null>(null);

// Create root store instance
export const rootStore = new RootStore();

// React hooks for accessing stores
export const useStores = (): RootStore => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useStores must be used within a StoreProvider');
  }
  return store;
};

export const useUserStore = (): UserStore => useStores().userStore;
export const useWorkspaceStore = (): WorkspaceStore =>
  useStores().workspaceStore;
export const useBillingStore = (): BillingStore => useStores().billingStore;
export const useEventInstanceStore = (): EventInstanceStore =>
  useStores().eventInstanceStore;
export const useScheduleStore = (): ScheduleStore => useStores().scheduleStore;
export const useSkillLevelsStore = (): SkillLevelsStore =>
  useStores().skillLevelsStore;
export const useNavigationService = (): NavigationService =>
  useStores().navigationService;
